import { useMemo, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { logout } from "@/store/slices/authSlice";
import { useTheme } from "@/components/common/ThemeProvider";
import { useAzureAuth } from "@/hooks/useAzureAuth";
import { Typography } from "../ui/typography";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Moon, LogOut } from "lucide-react";
import downArrowIcon from "@/assets/svg/downArrowIcon.svg";
import editLayoutIcon from "@/assets/svg/editlayout.svg";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATH } from "@/constants/routeConstants";
import {
  clearUserStatus,
  fetchUserStatus,
} from "@/store/slices/userStatusSlice";
import { clearDashboardState } from "@/store/slices/dashboardSlice";
import { useAppDispatch } from "@/store/hook";
import { TruncatedTextTooltip } from "./TextTooltip";

function getInitials(displayName?: string): string {
  if (!displayName) return "SJ";

  const names = displayName.trim().split(" ");
  if (names.length === 0) return "SJ";

  const firstInitial = names[0]?.[0] || "S";
  const lastInitial =
    names.length > 1 ? names[names.length - 1]?.[0] || "J" : "";

  return `${firstInitial}${lastInitial}`.toUpperCase();
}

type UserMenuProps = {
  defaultOpen?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
};

type UserIdentityProps = {
  initials: string;
  displayName: string;
  agencyDisplay: string;
  avatarBgClass?: string;
  avatarSize: number;
};

const UserIdentity: React.FC<UserIdentityProps> = ({
  initials,
  displayName,
  agencyDisplay,
  avatarBgClass = "",
  avatarSize,
}) => (
  <div className='flex items-center gap-2 px-2 py-1.5 bg-transparent'>
    <div
      className={`flex items-center justify-center rounded text-white flex-shrink-0 ${avatarBgClass}`}
      style={{
        width: avatarSize,
        height: avatarSize,
        backgroundColor: "hsl(var(--primary-700))",
      }}>
      <span className='text-sm font-semibold select-none'>{initials}</span>
    </div>
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "column",
        alignItems: "flex-start",
      }}>
      <div className='flex flex-col text-left'>
        <Typography variant='semibold-14'>{displayName}</Typography>
      </div>

      <TruncatedTextTooltip text={agencyDisplay} className='flex-1 min-w-0'>
        <Typography
          variant='medium-12'
          className='text-grey-600 dark:text-primary-300 truncate max-w-[130px]'>
          {agencyDisplay}
        </Typography>
      </TruncatedTextTooltip>
    </div>
  </div>
);

type ThemeToggleRowProps = {
  theme: string;
  toggleTheme: () => void;
};

const ThemeToggleRow: React.FC<ThemeToggleRowProps> = ({ theme, toggleTheme }) => (
  <div className='flex items-center justify-between w-full'>
    <div className='flex items-center gap-3'>
      <Moon className='h-[18px] w-[18px] text-grey-600 dark:text-grey-400' />
      <Typography variant='medium-14'>Dark Mode</Typography>
    </div>
    <SwitchPrimitives.Root
      checked={theme === "dark"}
      onCheckedChange={() => toggleTheme()}
      className='
        group switch-no-ring relative inline-flex w-[30px] h-[18px] items-center
        rounded-full p-[3px] border transition-colors
        data-[state=checked]:bg-primary-500 data-[state=unchecked]:bg-white
        data-[state=checked]:border-primary-500 data-[state=unchecked]:border-grey-300
        outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:shadow-none
      '>
      <SwitchPrimitives.Thumb
        className='
          block h-[12px] w-[12px] rounded-full bg-grey-400
          group-data-[state=checked]:bg-white
          transition-transform duration-200 ease-out
          group-data-[state=checked]:translate-x-[12px]
        '
      />
    </SwitchPrimitives.Root>
  </div>
);

export default function UserMenu({
  defaultOpen = false,
  isMobile = false,
  onClose,
  buttonRef,
}: UserMenuProps) {
  const dispatch = useAppDispatch();
  const { theme, toggleTheme } = useTheme();
  const { logout: azureLogout } = useAzureAuth();
  const user = useSelector((state: RootState) => state.auth.user);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const navigate = useNavigate();
  const userStatus = useSelector((state: RootState) => state.userStatus);

  const displayName =
    userStatus.displayName ||
    `${user?.first_name ?? "Sarah"} ${user?.last_name ?? "James"}`;

  const initials = useMemo(
    () =>
      getInitials(
        userStatus.displayName || `${user?.first_name} ${user?.last_name}`
      ),
    [userStatus.displayName, user?.first_name, user?.last_name]
  );

  const agencyDisplay = useMemo(() => {
    // Display all agencies separated by comma
    if (userStatus.agencies && userStatus.agencies.length > 0) {
      return userStatus.agencies.join(", ");
    }
    // If no agencies, display first role
    if (userStatus.roles && userStatus.roles.length > 0) {
      return userStatus.roles[0];
    }
    return "No Agency";
  }, [userStatus.agencies, userStatus.roles]);

  /**
   * Handle logout - Logs out from both Redux and Azure Entra ID
   * HIPAA Compliant: Logs audit event
   */
  const handleLogout = async () => {
    try {
      // Clear Redux state — dashboard transient state is cleared but
      // hasPreferredLayout is preserved so it can be re-derived from the
      // backend on the next login.
      dispatch(logout());
      dispatch(clearUserStatus());
      dispatch(clearDashboardState());

      // Logout from Azure Entra ID (skips Microsoft sign-out page by default)
      await azureLogout("USER_INITIATED"); // or azureLogout('USER_INITIATED', true)

      // Navigate directly to login page
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login", { replace: true });
    }
  };

  // Click outside handler for mobile
  useEffect(() => {
    if (!isMobile || !onClose) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Don't close if clicking on the button that opens the menu
        if (
          buttonRef?.current &&
          buttonRef.current.contains(event.target as Node)
        ) {
          return;
        }
        onClose();
      }
    };

    // Add a small delay to prevent immediate closing
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, onClose, buttonRef]);

  useEffect(() => {
    // Fetch user status if not already loaded
    if (!userStatus.displayName && !userStatus.isLoading) {
      dispatch(fetchUserStatus());
    }
  }, [dispatch, userStatus.displayName, userStatus.isLoading]);

  // For mobile, render without dropdown wrapper - just show the content directly
  if (isMobile) {
    return (
      <div ref={menuRef} className='flex flex-col gap-2 w-full'>
        <UserIdentity
          initials={initials}
          displayName={displayName}
          agencyDisplay={agencyDisplay}
          avatarSize={32}
        />

        {/* Separator */}
        {/* <div className='h-px bg-grey-300 dark:bg-grey-700 mx-0' /> */}

        {/* Menu Items */}
        {/* <div className='flex items-center justify-start cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-2 gap-3 w-full text-left'>
          <User className='h-[18px] w-[18px] text-grey-600 dark:text-grey-400' />
          <Typography variant='medium-14'>Profile</Typography>
        </div> */}

        <div className='h-px bg-grey-300 dark:bg-grey-700 mx-0' />

        <div className='px-2 py-2 gap-3 w-full'>
          <ThemeToggleRow theme={theme} toggleTheme={toggleTheme} />
        </div>

        {/* <div className='h-px bg-grey-300 dark:bg-grey-700 mx-0' />

        <div className='flex items-center justify-start cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-2 gap-3 w-full text-left'>
          <Settings className='h-[18px] w-[18px] text-grey-600 dark:text-grey-400' />
          <Typography variant='medium-14'>Settings</Typography>
        </div> */}

        <div className='h-px bg-grey-300 dark:bg-grey-700 mx-0' />

        <div
          onClick={() => navigate(ROUTE_PATH.ROUTE_EDIT_LAYOUT)}
          className='flex items-center justify-start cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-2 gap-3 w-full text-left'>
          <img src={editLayoutIcon} alt='' className='h-[18px] w-[18px] dark:invert' />
          <Typography variant='medium-14'>Edit Layout</Typography>
        </div>

        <div className='h-px bg-grey-300 dark:bg-grey-700 mx-0' />

        <div
          color='danger'
          // onClick={() => dispatch(logout())}
          onClick={handleLogout}
          className='flex items-center justify-start cursor-pointer text-danger-900 dark:text-danger-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-2 gap-3 w-full text-left'>
          <LogOut className='h-[18px] w-[18px] text-danger-900 dark:text-danger-500' />
          <Typography variant='medium-14'>Logout</Typography>
        </div>
      </div>
    );
  }

  // Desktop: Use normal dropdown
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          className='
            flex items-center gap-2 px-2 py-1.5 rounded-lg bg-transparent transition-colors
            outline-none focus:outline-none hover:outline-none border-none
            focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0
            data-[state=open]:outline-none data-[state=open]:ring-0 data-[state=open]:ring-offset-0
            hover:bg-transparent !ring-0 !outline-none'>
          <UserIdentity
            initials={initials}
            displayName={displayName}
            agencyDisplay={agencyDisplay}
            avatarBgClass='rounded-md'
            avatarSize={40}
          />

          {/* Name + role */}

          {/* Chevron */}
          <div
            style={{
              width: "21px",
              height: "21px",
              padding: "2px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "3px",
            }}>
            <img
              src={downArrowIcon}
              alt=''
              width='13px'
              height='13px'
              className={`ml-1 transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"} ${theme === "dark" ? "filter invert-[92%] sepia-0 saturate-0 hue-rotate-180 brightness-95 contrast-95" : ""}`}
            />
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align='end'
        className='w-[224px] rounded-[6px] p-[8px] bg-white dark:bg-blue-800 shadow-md outline-none focus:outline-none'>
        <div className='flex flex-col gap-2 h-full'>
          {/* <DropdownMenuItem className='cursor-pointer hover:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 px-2 py-2 gap-3'>
            <User className='h-[18px] w-[18px] text-grey-600 dark:text-grey-400' />
            <Typography variant='medium-14'>Profile</Typography>
          </DropdownMenuItem> */}

          {/* <DropdownMenuSeparator className=' h-px bg-grey-300 dark:bg-grey-700 mx-0' /> */}

          <DropdownMenuItem
            onSelect={(e) => e.preventDefault()}
            className='cursor-default hover:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 px-1 py-2 gap-3'>
            <ThemeToggleRow theme={theme} toggleTheme={toggleTheme} />
          </DropdownMenuItem>

          {/* <DropdownMenuSeparator className=' h-px bg-grey-300 dark:bg-grey-700 mx-0' />

          <DropdownMenuItem className='cursor-pointer hover:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 px-2 py-2 gap-3'>
            <Settings className='h-[18px] w-[18px] text-grey-600 dark:text-grey-400' />
            <Typography variant='medium-14'>Settings</Typography>
          </DropdownMenuItem> */}

          <DropdownMenuSeparator className=' h-px bg-grey-300 dark:bg-grey-700 mx-0' />

          <DropdownMenuItem
            onSelect={() => navigate(ROUTE_PATH.ROUTE_EDIT_LAYOUT)}
            className='cursor-pointer hover:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 px-2 py-2 gap-3'>
            <img src={editLayoutIcon} alt='' className='h-[18px] w-[18px] dark:invert' />
            <Typography variant='medium-14'>Edit Layout</Typography>
          </DropdownMenuItem>

          <DropdownMenuSeparator className=' h-px bg-grey-300 dark:bg-grey-700 mx-0' />

          <DropdownMenuItem
            onSelect={handleLogout}
            className='cursor-pointer !text-danger-900 dark:!text-danger-500 hover:!text-danger-900 dark:hover:!text-danger-500 hover:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 px-2 py-2 gap-3'>
            <LogOut className='h-[18px] w-[18px] text-danger-900 dark:text-danger-500' />
            <Typography
              variant='medium-14'
              className='!text-danger-900 dark:!text-danger-500'>
              Logout
            </Typography>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
